/**
 * Event page (Oct 11, 2026 — Dennis; claude/Event_Page_Plan_Oct11.md, step 1).
 *
 * PUBLIC, no login: /e/<event address>. TTOgolf.com/<address> redirects here
 * (server.js short domains). Every word comes from the organizer's Event page tab
 * (EventPageSetup.js); TTO supplies the template and the "Event page by Tee Time
 * Organizer" mark. The API is GET /api/public/event-page/:slug.
 *
 * Three moments, decided by the visitor's own date against the event's date:
 *   before  → Register (when he set one up) + "Follow it live on <date>"
 *   the day → the main button is Live leaderboard
 *   after   → the main button is See the results (the same board address)
 *
 * EventPageView is also the live preview inside the organizer's editor.
 *
 * Oct 23, 2026 (Dennis): a Donate button in the hero (the organizer's own link and
 * wording — it opens HIS page in a new tab; TTO never handles money) and a Sponsors grid
 * at the bottom: every tile looks the same — logo (or the sponsor's initials) with the
 * name under it; a tile with a website opens it in a new tab and shows a small ↗.
 *
 * Oct 23, 2026 (Dennis): "Entry & how to pay" — the organizer's prices and the ways he
 * collects (Venmo, PayPal, Cash App, Zelle, check, at check-in, his own pay page), shown
 * only when at least one way to pay is ticked. One-tap links with the amount and a note;
 * TTO never touches the money. payOptions() here builds the buttons for this page, the
 * editor's preview and the sign-up confirmation (App.js); the backend's twin builds the
 * confirmation email (services/eventPaymentService.js) — keep the two in step.
 */
import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === "production" ? "/api" : "http://localhost:3001/api");

const FONT_HREF = "https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@500;600;700&family=Source+Sans+3:wght@400;500;600;700&display=swap";
const HEAD = "'Barlow Condensed', 'Arial Narrow', sans-serif";
const BODY = "'Source Sans 3', system-ui, -apple-system, 'Segoe UI', sans-serif";
const PAPER = "#F6F4EE";
const INK = "#1D2420";
const MUTED = "#5B655F";
const CARD_SHADOW = "0 1px 2px rgba(20,30,25,0.06), 0 8px 24px rgba(20,30,25,0.05)";

export function useEventPageFonts() {
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (document.querySelector('link[data-tto-event-fonts]')) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = FONT_HREF;
    link.setAttribute("data-tto-event-fonts", "1");
    document.head.appendChild(link);
  }, []);
}

const pad2 = (n) => String(n).padStart(2, "0");
const localYmd = (d = new Date()) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** 'before' | 'today' | 'after' from the event's YYYY-MM-DD and the visitor's date. */
export function phaseFor(eventDate, today = localYmd()) {
  if (!eventDate) return "before";
  if (eventDate > today) return "before";
  if (eventDate === today) return "today";
  return "after";
}

function shortDate(ymd, long = false) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(ymd || ""))) return "";
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString("en-US", { month: long ? "long" : "short", day: "numeric" });
}

// Plain text → paragraphs; blank line = new paragraph, single line break kept;
// web addresses and emails become links.
// Bare addresses too (seacoast.org/turkeyshoot) for the common endings.
const LINK_RE = /(https?:\/\/[^\s<]+|www\.[^\s<]+|[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}|\b[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.(?:com|org|net|edu|gov|us|church|golf|club|info|co|events?)\b(?:\/[^\s<]*)?)/g;
function linkify(text, keyBase, linkStyle) {
  const out = [];
  let last = 0;
  let m;
  let i = 0;
  LINK_RE.lastIndex = 0;
  while ((m = LINK_RE.exec(text))) {
    let raw = m[0];
    let trail = "";
    while (/[.,;:!?)\]'"]$/.test(raw)) { trail = raw.slice(-1) + trail; raw = raw.slice(0, -1); }
    if (m.index > last) out.push(text.slice(last, m.index));
    const isEmail = raw.includes("@") && !/^https?:/i.test(raw);
    const href = isEmail ? `mailto:${raw}` : (/^https?:/i.test(raw) ? raw : `https://${raw}`);
    out.push(<a key={`${keyBase}-${i++}`} href={href} target={isEmail ? undefined : "_blank"} rel="noopener noreferrer" style={linkStyle}>{raw}</a>);
    if (trail) out.push(trail);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** "a | b", "a; b", "a / b" → ["a", "b"] (a phone like 843/555-0100 is left whole). */
export function splitContact(v) {
  const s = String(v || "").trim();
  if (!s) return [];
  return s.split(/\s*[|;]\s*|\s+\/\s+|\s*,\s*(?=\S+@)/).map((x) => x.trim()).filter(Boolean);
}

function Paragraphs({ text, style, linkStyle }) {
  const paras = String(text || "").split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, ...style }}>
      {paras.map((p, pi) => (
        <p key={pi} style={{ margin: 0, whiteSpace: "pre-line" }}>{linkify(p, `p${pi}`, linkStyle)}</p>
      ))}
    </div>
  );
}

const Icon = ({ d, color, size = 18, children }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    {d ? <path d={d} /> : children}
  </svg>
);
const CalendarIcon = ({ color }) => (<Icon color={color}><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 10h18" /></Icon>);
const PinIcon = ({ color }) => (<Icon color={color}><path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z" /><circle cx="12" cy="9.5" r="2.5" /></Icon>);
const ClockIcon = ({ color }) => (<Icon color={color}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></Icon>);
const ArrowIcon = ({ color }) => (<Icon color={color} d="M5 12h14M13 6l6 6-6 6" />);
const HeartIcon = ({ color }) => (<Icon color={color} d="M12 20s-7-4.4-9.2-9A5 5 0 0 1 12 6.3 5 5 0 0 1 21.2 11C19 15.6 12 20 12 20z" />);
const ExternalIcon = ({ color, size = 13 }) => (<Icon color={color} size={size} d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />);

/** "Acme Roofing & Supply" → "AR" — the tile for a sponsor with no logo. */
export function sponsorInitials(name) {
  const words = String(name || "").replace(/[^A-Za-z0-9 ]+/g, " ").split(/\s+/).filter(Boolean);
  const skip = /^(the|and|of|a|an|inc|llc|co)$/i;
  const main = words.filter((w) => !skip.test(w));
  const pick = (main.length ? main : words).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
  return pick || "★";
}

// ---------------------------------------------------------------------------
// Payment links (Oct 23, 2026). Mirrors backend services/eventPaymentService.js payOptions.
// ---------------------------------------------------------------------------
/** "$1,250.5" → "1250.50"; "150" → "150"; anything else → "". */
export function payAmount(raw) {
  const s = String(raw == null ? "" : raw).replace(/[$,\s]/g, "");
  if (!/^\d{1,6}(\.\d{1,2})?$/.test(s)) return "";
  const n = Number(s);
  if (!(n > 0)) return "";
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}
/** "150" → "$150"; "1250.5" → "$1,250.50". */
export function money(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return "";
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: 2 })}`;
}
/** "Turkey Shoot 2026" + "Single player" → "Turkey Shoot 2026 – Single player". */
export function payNote(eventName, who) {
  const cut = (v) => String(v || "").replace(/\s+/g, " ").trim().slice(0, 40);
  return [cut(eventName), cut(who)].filter(Boolean).join(" – ");
}
/** The buttons/lines for one amount + note → [{ type, kind: "link"|"text", title, href?, lines }]. */
export function payOptions({ methods, amount, note }) {
  const amt = payAmount(amount);
  const m$ = money(amt);
  const out = [];
  for (const m of methods || []) {
    if (!m) continue;
    if (m.type === "venmo" && m.handle) {
      const q = ["txn=pay"];
      if (amt) q.push(`amount=${amt}`);
      if (note) q.push(`note=${encodeURIComponent(note)}`);
      out.push({ type: m.type, kind: "link", title: `Pay ${m$ ? `${m$} ` : ""}with Venmo`, href: `https://venmo.com/${encodeURIComponent(m.handle)}?${q.join("&")}`, lines: [`To @${m.handle}`] });
    } else if (m.type === "paypal" && m.handle) {
      out.push({ type: m.type, kind: "link", title: `Pay ${m$ ? `${m$} ` : ""}with PayPal`, href: `https://paypal.me/${encodeURIComponent(m.handle)}${amt ? `/${amt}` : ""}`, lines: note ? [`Add this note: ${note}`] : [] });
    } else if (m.type === "cashapp" && m.handle) {
      out.push({ type: m.type, kind: "link", title: `Pay ${m$ ? `${m$} ` : ""}with Cash App`, href: `https://cash.app/$${encodeURIComponent(m.handle)}${amt ? `/${amt}` : ""}`, lines: [`To $${m.handle}`].concat(note ? [`Add this note: ${note}`] : []) });
    } else if (m.type === "zelle" && m.handle) {
      out.push({ type: m.type, kind: "text", title: `Zelle${m$ ? ` ${m$}` : ""}`, lines: [`Send to ${m.handle} from your bank’s app`].concat(note ? [`Memo: ${note}`] : []) });
    } else if (m.type === "check" && m.handle) {
      out.push({ type: m.type, kind: "text", title: `Check${m$ ? ` for ${m$}` : ""}`, lines: [`Payable to ${m.handle}`].concat(m.note ? [`Mail to: ${String(m.note).replace(/\n/g, ", ")}`] : []).concat(note ? [`Memo line: ${note}`] : []) });
    } else if (m.type === "checkin") {
      out.push({ type: m.type, kind: "text", title: `Pay ${m$ ? `${m$} ` : ""}at check-in`, lines: m.note ? [m.note] : [] });
    } else if (m.type === "link" && m.url) {
      out.push({ type: m.type, kind: "link", title: m.label || "Pay online", href: m.url, lines: m$ ? [`Amount: ${m$}`] : [] });
    }
  }
  return out;
}

/**
 * The pay buttons themselves — big, one per line, for older golfers on phones. Used on the
 * event page and on the sign-up confirmation (App.js). Links open the payment app/site in
 * a new tab (preview: never navigates the editor).
 */
export function PayButtons({ options, accent = "#1F5C3F", accentInk = "#FFFFFF", font }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {(options || []).map((o) => (
        <div key={o.type}>
          {o.kind === "link" ? (
            <a href={o.href} target="_blank" rel="noopener noreferrer" data-testid={`pay-${o.type}`}
              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, minHeight: 54, padding: "10px 18px", borderRadius: 10, background: accent, color: accentInk, fontWeight: 700, fontSize: 18, textDecoration: "none", boxSizing: "border-box", textAlign: "center", fontFamily: font }}>
              {o.title}<ExternalIcon color={accentInk} size={15} />
            </a>
          ) : (
            // Not a button -- no link exists (Zelle, check, at check-in) -- so it reads as a note.
            <div data-testid={`pay-${o.type}`} style={{ borderLeft: "4px solid #1F5C3F", borderRadius: 6, padding: "10px 14px", background: "#F1EFE8", fontFamily: font }}>
              <div style={{ fontWeight: 700, fontSize: 18, color: "#1D2420" }}>{o.title}</div>
              {o.lines.length > 0 && (
                <div style={{ fontSize: 15, color: "#4A544E", lineHeight: 1.5, marginTop: 4, overflowWrap: "anywhere" }}>
                  {o.lines.map((l, i) => <div key={i}>{l}</div>)}
                </div>
              )}
            </div>
          )}
          {o.kind === "link" && o.lines.length > 0 && (
            <div style={{ fontSize: 15, color: "#4A544E", lineHeight: 1.5, marginTop: 6, paddingLeft: 4, overflowWrap: "anywhere" }}>
              {o.lines.map((l, i) => <div key={i}>{l}</div>)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/** "Entry & how to pay" on the event page. Several prices → the golfer picks which first. */
function PaymentSection({ payment, narrow, card, h2Big }) {
  const fees = (payment && payment.fees) || [];
  const [pick, setPick] = useState(0);
  const methods = (payment && payment.methods) || [];
  if (!methods.length) return null;
  const fee = fees[Math.min(pick, Math.max(0, fees.length - 1))] || null;
  const note = payNote(payment.event_name, fee ? fee.label : "");
  const options = payOptions({ methods, amount: fee ? fee.amount : "", note });
  if (!options.length) return null;
  return (
    <section aria-labelledby="evp-pay" data-testid="evp-pay" style={{ ...card, padding: narrow ? "22px 20px" : "32px 40px" }}>
      <h2 id="evp-pay" style={h2Big}>{fees.length ? "Entry & how to pay" : "How to pay"}</h2>
      {fees.length > 1 && (
        <div role="radiogroup" aria-label="What are you paying for?" style={{ display: "grid", gridTemplateColumns: narrow ? "1fr" : "repeat(auto-fit, minmax(200px, 1fr))", gap: 10, marginBottom: 18 }}>
          {fees.map((f, i) => {
            const on = i === pick;
            return (
              <button key={i} type="button" role="radio" aria-checked={on} onClick={() => setPick(i)}
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, minHeight: 56, padding: "10px 16px", borderRadius: 10, cursor: "pointer", fontFamily: BODY, fontSize: 17, textAlign: "left",
                  border: on ? "2.5px solid #1F5C3F" : "1.5px solid #CBD5CF", background: on ? "#E6EFE9" : "#FFFFFF", color: INK }}>
                <span style={{ fontWeight: 600 }}>{on ? "● " : "○ "}{f.label}</span>
                <span style={{ fontWeight: 700, whiteSpace: "nowrap" }}>{money(f.amount)}</span>
              </button>
            );
          })}
        </div>
      )}
      {fees.length === 1 && (
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: narrow ? 18 : 20, fontWeight: 600, marginBottom: 16, padding: "10px 0", borderBottom: "1px solid #E2DED3" }}>
          <span>{fee.label}</span><span style={{ fontWeight: 700 }}>{money(fee.amount)}</span>
        </div>
      )}
      <div style={{ maxWidth: 520 }}>
        <PayButtons options={options} font={BODY} />
      </div>
      <p style={{ margin: "16px 0 0", fontSize: 14, color: MUTED, lineHeight: 1.5 }}>
        Payments go directly to {payment.payee || "the organizer"}. Tee Time Organizer does not handle payments — questions about paying go to the organizer.
      </p>
    </section>
  );
}

/** The Sponsors grid. Same tile for every sponsor; a link only changes the ↗ and the tag. */
function Sponsors({ sponsors, narrow, apiAbs }) {
  const list = (sponsors || []).filter((s) => s && s.name);
  if (list.length === 0) return null;
  const tile = {
    display: "flex", flexDirection: "column", alignItems: "stretch", gap: 10, padding: narrow ? 12 : 16,
    background: "#FFFFFF", borderRadius: 12, boxShadow: CARD_SHADOW, textDecoration: "none", color: INK,
    boxSizing: "border-box", minWidth: 0, height: "100%",
  };
  const boxH = narrow ? 64 : 84;
  return (
    <section aria-labelledby="evp-sponsors" style={{ display: "flex", flexDirection: "column", gap: narrow ? 14 : 18 }}>
      <h2 id="evp-sponsors" style={{ margin: 0, fontFamily: HEAD, fontSize: narrow ? 22 : 26, fontWeight: 600, letterSpacing: "0.03em", textTransform: "uppercase", color: INK, textAlign: "center" }}>
        Thank you to our sponsors
      </h2>
      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gridTemplateColumns: narrow ? "repeat(2, minmax(0, 1fr))" : "repeat(auto-fill, minmax(170px, 1fr))", gap: narrow ? 12 : 16 }}>
        {list.map((sp, i) => {
          const logo = sp.logo_url ? apiAbs(sp.logo_url) : null;
          const inner = (
            <>
              <div style={{ height: boxH, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                {logo
                  ? <img src={logo} alt="" loading="lazy" style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain", display: "block" }} />
                  : <div aria-hidden="true" style={{ height: boxH, width: boxH, borderRadius: 12, background: "#E6EFE9", color: "#1F5C3F", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: HEAD, fontWeight: 700, fontSize: narrow ? 26 : 32, letterSpacing: "0.04em" }}>{sponsorInitials(sp.name)}</div>}
              </div>
              <div style={{ fontSize: narrow ? 14 : 15, fontWeight: 600, lineHeight: 1.3, textAlign: "center", minHeight: 20, overflowWrap: "anywhere" }}>
                {sp.name}
                {sp.url && <span style={{ display: "inline-block", marginLeft: 5, verticalAlign: "-1px", whiteSpace: "nowrap" }}><ExternalIcon color={MUTED} /></span>}
              </div>
            </>
          );
          return (
            <li key={`${i}-${sp.name}`} style={{ minWidth: 0 }}>
              {sp.url
                ? <a href={sp.url} target="_blank" rel="noopener noreferrer" style={tile} title={`${sp.name} — opens their website`}>{inner}</a>
                : <div style={tile}>{inner}</div>}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function Contours({ color }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 1200 520" preserveAspectRatio="xMidYMid slice" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.14 }}>
      <g fill="none" stroke={color} strokeWidth="1.2">
        <path d="M-20 420 C 180 360, 320 470, 520 400 S 860 300, 1220 380" />
        <path d="M-20 380 C 200 320, 330 430, 540 360 S 880 260, 1220 340" />
        <path d="M-20 340 C 220 280, 350 390, 560 320 S 900 220, 1220 300" />
        <path d="M-20 300 C 240 240, 370 350, 580 280 S 920 180, 1220 260" />
        <path d="M600 120 C 700 60, 860 80, 900 150 S 820 260, 700 240 S 520 180, 600 120 Z" />
        <path d="M640 135 C 720 95, 830 110, 855 160 S 800 225, 710 212 S 580 170, 640 135 Z" />
        <path d="M680 150 C 735 125, 800 135, 812 168 S 780 200, 725 194 S 645 175, 680 150 Z" />
      </g>
    </svg>
  );
}

/**
 * The page itself. `data` is the public API answer (or the editor's preview of it).
 * narrow: phone layout. phase: override (editor preview); default from the date.
 * preview: links open in a new tab and nothing scrolls the editor.
 */
export function EventPageView({ data, narrow = false, phase: phaseIn, preview = false }) {
  const d = data || {};
  const bg = d.background || { kind: "color", bg: "#173F2C", ink: "#F4F1E8", accent: "#D9B45A", accentInk: INK };
  const isPhoto = bg.kind === "photo";
  const ink = bg.ink || "#FFFFFF";
  const accent = bg.accent || "#E3BC5E";
  const accentInk = bg.accentInk || INK;
  const phase = phaseIn || phaseFor(d.event_date);
  const reg = d.registration;
  const lb = d.leaderboard;
  const donate = d.donate && d.donate.url ? d.donate : null;
  const day = shortDate(d.event_date);
  const dayLong = shortDate(d.event_date, true);
  const target = preview ? "_blank" : undefined;
  const followRef = useRef(null);

  let chip = reg ? "Registration open" : (d.date_text || "");
  // Oct 12, 2026: this event's own TTO sign-up link (/invite/<code>) opens in the same tab and
  // carries ?from=<address>, so the invite page can offer "Back to event page" and Back works.
  // An organizer's own outside registration link still opens in a new tab.
  const isInvite = !!(reg && /\/invite\/[^/?#]+\/?$/.test(reg.url || ""));
  const regHref = reg && isInvite && d.slug ? `${reg.url}?from=${encodeURIComponent(d.slug)}` : (reg && reg.url);
  let primary = reg ? { href: regHref, label: reg.label || "Register", external: !isInvite } : null;
  // The board opens in THIS tab with ?event=<address> (Oct 22, 2026 — a player on a phone
  // had no way back: the new tab hid the event page and the board had no Back). The board
  // shows "← Event page" for it, and the phone's Back button works too.
  const lbHref = lb && lb.url ? (d.slug ? `${lb.url}${lb.url.includes("?") ? "&" : "?"}event=${encodeURIComponent(d.slug)}` : lb.url) : null;
  let secondary = lb ? { href: "#follow", label: day ? `Follow it live on ${day}` : "Follow it live" } : null;
  let followTitle = dayLong ? `Follow it live on ${dayLong}` : "Follow it live";
  let followText = "Scores update hole by hole on any phone. No app or account needed.";
  if (phase === "today") {
    chip = "Today";
    primary = lb ? { href: lbHref, label: "Live leaderboard", external: false } : null;
    secondary = null;
    followTitle = "Live today";
    followText = "Scores are coming in from the course. The board refreshes by itself.";
  } else if (phase === "after") {
    chip = lb ? "Final results" : (d.date_text || "");
    primary = lb ? { href: lbHref, label: "See the results", external: false } : null;
    secondary = null;
    followTitle = "Final results";
    followText = "The final leaderboard, posted after the round.";
  }

  const scrollToFollow = (e) => {
    e.preventDefault();
    if (followRef.current && followRef.current.scrollIntoView) followRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const details = [
    ["Date", d.date_text],
    ["Course", d.course_text],
    ["Check-in", d.checkin_text],
    ["Start", d.start_text],
    ["Format", d.format_text],
  ].filter(([, v]) => v);
  const contact = d.contact;
  const linkStyle = { color: "#1F5C3F", fontWeight: 600 };
  const h2Big = { margin: "0 0 16px", fontFamily: HEAD, fontSize: narrow ? 24 : 30, fontWeight: 600, letterSpacing: "0.02em", textTransform: "uppercase", color: INK };
  const h2Small = { margin: "0 0 12px", fontFamily: HEAD, fontSize: narrow ? 20 : 22, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", color: INK };
  const card = { background: "#FFFFFF", borderRadius: narrow ? 14 : 16, boxShadow: CARD_SHADOW, boxSizing: "border-box" };
  const logoSrc = d.logo_url ? (/^https?:/.test(d.logo_url) || d.logo_url.startsWith("blob:") ? d.logo_url : `${API_URL}${d.logo_url}`) : null;
  // The organizer's own photo comes from the API (/public/event-page/...); library photos are static files.
  const apiAbs = (u) => (u && u.startsWith("/public/") ? `${API_URL}${u}` : u);
  const bgUrl = isPhoto ? apiAbs(narrow ? (bg.url_small || bg.url) : bg.url) : null;
  const btnBase = { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 10, minHeight: 52, padding: "0 26px", borderRadius: 10, fontSize: 17, textDecoration: "none", boxSizing: "border-box" };

  return (
    <div style={{ fontFamily: BODY, color: INK, background: PAPER, minHeight: preview ? undefined : "100vh" }}>
      <header style={{ position: "relative", overflow: "hidden", background: bg.bg || "#173F2C", color: ink }}>
        {isPhoto && (
          <>
            <div aria-hidden="true" style={{ position: "absolute", inset: 0, backgroundImage: `url(${bgUrl})`, backgroundSize: "cover", backgroundPosition: bg.position || "center", backgroundRepeat: "no-repeat" }} />
            <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: `rgba(10,20,14,${bg.scrim != null ? bg.scrim : 0.5})` }} />
          </>
        )}
        {!isPhoto && <Contours color={ink} />}
        <div style={{ position: "relative", maxWidth: 1120, margin: "0 auto", padding: narrow ? "20px 20px 36px" : "28px 32px 72px", boxSizing: "border-box", display: "flex", flexDirection: "column", gap: narrow ? 30 : 56 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
              {logoSrc && (
                // Oct 12, 2026: bigger, and the box takes the logo's shape -- a round or square logo
                // stays square, a wide wordmark gets a wider box instead of shrinking into a square.
                <div style={{ height: narrow ? 72 : 112, minWidth: narrow ? 72 : 112, maxWidth: narrow ? 180 : 260, borderRadius: narrow ? 12 : 16, background: "rgba(255,255,255,0.94)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0, padding: narrow ? 6 : 8, boxSizing: "border-box", boxShadow: "0 2px 10px rgba(0,0,0,0.18)" }}>
                  <img src={logoSrc} alt="" style={{ height: "100%", width: "auto", maxWidth: narrow ? 168 : 244, objectFit: "contain", display: "block" }} />
                </div>
              )}
              {d.presented_by && !narrow && (
                <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", opacity: 0.92 }}>{d.presented_by} presents</div>
              )}
            </div>
            {chip && (
              <div style={{ fontSize: narrow ? 13 : 14, fontWeight: 600, padding: narrow ? "6px 12px" : "7px 14px", borderRadius: 999, background: bg.kind === "color" && bg.key === "sand" ? "rgba(31,42,35,0.10)" : "rgba(255,255,255,0.18)", color: ink }}>{chip}</div>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: narrow ? 14 : 22, maxWidth: 820 }}>
            {d.presented_by && narrow && (
              <div style={{ fontSize: 13, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", opacity: 0.92 }}>{d.presented_by} presents</div>
            )}
            <h1 style={{ margin: 0, fontFamily: HEAD, fontWeight: 700, fontSize: narrow ? 48 : "clamp(48px, 7vw, 92px)", lineHeight: 0.95, letterSpacing: "0.01em", textTransform: "uppercase", overflowWrap: "anywhere", textShadow: isPhoto ? "0 1px 18px rgba(0,0,0,0.25)" : undefined }}>{d.title}</h1>
            <div style={{ display: "flex", flexDirection: narrow ? "column" : "row", flexWrap: "wrap", gap: narrow ? 8 : "10px 28px", fontSize: narrow ? 17 : 19, fontWeight: 500 }}>
              {d.date_text && <span style={{ display: "flex", alignItems: "center", gap: 8 }}><CalendarIcon color={accent} />{d.date_text}</span>}
              {d.course_text && <span style={{ display: "flex", alignItems: "center", gap: 8 }}><PinIcon color={accent} />{d.course_text}</span>}
              {d.start_text && <span style={{ display: "flex", alignItems: "center", gap: 8 }}><ClockIcon color={accent} />{d.start_text}</span>}
            </div>
            {(primary || secondary || donate) && (
              <div style={{ display: "flex", flexDirection: narrow ? "column" : "row", flexWrap: "wrap", gap: narrow ? 12 : 14, marginTop: narrow ? 6 : 10 }}>
                {primary && (
                  <a href={primary.href} target={primary.external ? "_blank" : target} rel="noopener noreferrer"
                    style={{ ...btnBase, background: accent, color: accentInk, fontWeight: 700 }}>
                    {primary.label}<ArrowIcon color={accentInk} />
                  </a>
                )}
                {secondary && (
                  <a href={secondary.href} onClick={scrollToFollow}
                    style={{ ...btnBase, border: `1.5px solid ${ink}`, color: ink, fontWeight: 600, padding: "0 24px" }}>
                    {secondary.label}
                  </a>
                )}
                {donate && (
                  // The organizer's own page, always a new tab: the money happens there, not in TTO.
                  <a href={donate.url} target="_blank" rel="noopener noreferrer" data-testid="evp-donate"
                    style={{ ...btnBase, border: `1.5px solid ${ink}`, color: ink, fontWeight: 600, padding: "0 24px" }}>
                    <HeartIcon color={accent} />{donate.label || "Donate"}
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 1120, margin: "0 auto", padding: narrow ? "24px 16px" : "56px 32px 24px", boxSizing: "border-box", display: "flex", flexDirection: "column", gap: narrow ? 18 : 32 }}>
        <div style={{ display: "flex", flexDirection: narrow ? "column" : "row", flexWrap: "wrap", gap: narrow ? 18 : 28, alignItems: narrow ? "stretch" : "flex-start" }}>
          {d.description && (
            <section style={{ ...card, flex: narrow ? undefined : "2 1 520px", minWidth: 0, padding: narrow ? "22px 20px" : "36px 40px" }}>
              <h2 style={h2Big}>About the event</h2>
              <Paragraphs text={d.description} linkStyle={linkStyle} style={{ fontSize: narrow ? 16 : 18, lineHeight: 1.65, color: "#2E3631" }} />
            </section>
          )}
          {(details.length > 0 || contact) && (
            <aside style={{ flex: narrow ? undefined : "1 1 300px", minWidth: 0, display: "flex", flexDirection: "column", gap: narrow ? 18 : 20 }}>
              {details.length > 0 && (
                <div style={{ ...card, padding: narrow ? 20 : "26px 28px" }}>
                  <h2 style={h2Small}>Event details</h2>
                  <dl style={{ margin: 0, display: "grid", gridTemplateColumns: "auto 1fr", gap: "10px 16px", fontSize: narrow ? 15 : 16 }}>
                    {details.map(([k, v]) => (
                      <React.Fragment key={k}>
                        <dt style={{ color: MUTED, fontWeight: 600 }}>{k}</dt>
                        <dd style={{ margin: 0, overflowWrap: "anywhere" }}>{v}</dd>
                      </React.Fragment>
                    ))}
                  </dl>
                </div>
              )}
              {contact && (
                <div style={{ ...card, padding: narrow ? 20 : "26px 28px" }}>
                  <h2 style={h2Small}>Questions?</h2>
                  <div style={{ fontSize: 16, lineHeight: 1.6, display: "flex", flexDirection: "column", gap: 6 }}>
                    {contact.name && <div style={{ fontWeight: 600 }}>{contact.name}</div>}
                    {/* Two contacts may share a field: "(843) 555-0100 | (864) 555-0199" — each its own link. */}
                    {splitContact(contact.email).map((e, i) => <a key={`e${i}`} href={`mailto:${e}`} style={{ ...linkStyle, overflowWrap: "anywhere" }}>{e}</a>)}
                    {splitContact(contact.phone).map((ph, i) => (
                      /\d{7,}/.test(ph.replace(/\D/g, ""))
                        ? <a key={`p${i}`} href={`tel:${ph.replace(/[^\d+]/g, "")}`} style={linkStyle}>{ph}</a>
                        : <span key={`p${i}`}>{ph}</span>
                    ))}
                  </div>
                </div>
              )}
            </aside>
          )}
        </div>

        {d.payment && <PaymentSection payment={d.payment} narrow={narrow} card={card} h2Big={h2Big} />}

        {lb && (
          <section id="follow" ref={followRef} style={{ display: "flex", flexDirection: narrow ? "column" : "row", flexWrap: "wrap", alignItems: narrow ? "stretch" : "center", justifyContent: "space-between", gap: narrow ? 14 : 24, background: "#1D2420", color: "#F4F1E8", borderRadius: narrow ? 14 : 16, padding: narrow ? "22px 20px" : "30px 36px", scrollMarginTop: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 20, minWidth: 0 }}>
              {!narrow && (
                <div style={{ width: 52, height: 52, borderRadius: 12, background: "#2D6A4F", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon color="#F4F1E8" size={26} d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
                </div>
              )}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: HEAD, fontSize: narrow ? 24 : 26, fontWeight: 600, letterSpacing: "0.02em", textTransform: "uppercase" }}>{followTitle}</div>
                <div style={{ fontSize: narrow ? 15 : 16, color: "#C9CFCB", marginTop: 2 }}>{followText}</div>
              </div>
            </div>
            <a href={lb.url} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minHeight: 48, padding: "0 22px", borderRadius: 10, background: "#F4F1E8", color: INK, fontWeight: 700, fontSize: narrow ? 15 : 16, textDecoration: "none", overflowWrap: "anywhere", textAlign: "center" }}>{lb.address}</a>
          </section>
        )}

        <Sponsors sponsors={d.sponsors} narrow={narrow} apiAbs={apiAbs} />
      </main>

      <footer style={{ maxWidth: 1120, margin: "0 auto", padding: narrow ? "20px 16px 32px" : "28px 32px 40px", boxSizing: "border-box", display: "flex", flexDirection: narrow ? "column" : "row", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: narrow ? 12 : 16, borderTop: "1px solid #E2DED3", fontSize: 14, color: MUTED }}>
        <a href={(d.links && d.links.tto) || "/events"} target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none", color: "#2E3631" }}>
          <span style={{ width: 28, height: 28, borderRadius: "50%", background: "#2D6A4F", color: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>TTO</span>
          <span>Event page by <strong>Tee Time Organizer</strong></span>
        </a>
        <div style={{ display: "flex", gap: 20 }}>
          <a href={(d.links && d.links.login) || "/login"} target={target} rel="noopener noreferrer" style={{ color: MUTED }}>Organizer login</a>
          <a href={(d.links && d.links.desk) || "/desk"} target={target} rel="noopener noreferrer" style={{ color: MUTED }}>Event desk</a>
        </div>
      </footer>
    </div>
  );
}

function useNarrow(breakpoint = 720) {
  const get = () => (typeof window !== "undefined" ? window.innerWidth < breakpoint : false);
  const [narrow, setNarrow] = useState(get);
  useEffect(() => {
    const on = () => setNarrow(get());
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return narrow;
}

function Notice({ title, children }) {
  return (
    <div style={{ minHeight: "100vh", background: PAPER, fontFamily: BODY, color: INK, display: "flex", flexDirection: "column" }}>
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
        <div style={{ background: "#fff", borderRadius: 16, boxShadow: CARD_SHADOW, padding: "32px 28px", maxWidth: 480, width: "100%", boxSizing: "border-box", textAlign: "center" }}>
          <h1 style={{ margin: "0 0 10px", fontFamily: HEAD, fontSize: 32, fontWeight: 700, textTransform: "uppercase", lineHeight: 1 }}>{title}</h1>
          <div style={{ fontSize: 17, lineHeight: 1.55, color: "#2E3631" }}>{children}</div>
        </div>
      </div>
      <div style={{ textAlign: "center", padding: "0 16px 28px", fontSize: 14, color: MUTED }}>
        <a href="/events" style={{ color: MUTED }}>Event page by <strong>Tee Time Organizer</strong></a>
      </div>
    </div>
  );
}

export default function EventPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const narrow = useNarrow();
  const [state, setState] = useState({ loading: true });
  useEventPageFonts();

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true });
    (async () => {
      try {
        const res = await fetch(`${API_URL}/public/event-page/${encodeURIComponent(slug || "")}`);
        if (res.status === 404) { if (!cancelled) setState({ notFound: true }); return; }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        // An old address that still works: show the current one in the address bar.
        if (data.slug && data.slug !== slug) navigate(`/e/${data.slug}`, { replace: true });
        setState({ data });
      } catch (e) {
        if (!cancelled) setState({ error: true });
      }
    })();
    return () => { cancelled = true; };
  }, [slug, navigate]);

  const data = state.data;
  useEffect(() => {
    if (data && data.published && data.title) document.title = data.presented_by ? `${data.title} — ${data.presented_by}` : data.title;
    else if (data && data.event_name) document.title = data.event_name;
  }, [data]);

  if (state.loading) return <div style={{ minHeight: "100vh", background: PAPER }} />;
  if (state.notFound) {
    return (
      <Notice title="Event not found">
        No event has the address <strong>{slug}</strong>. Check the spelling on your flyer or email.
      </Notice>
    );
  }
  if (state.error) {
    return (
      <Notice title="Something went wrong">
        The event page couldn’t be loaded. Please try again in a minute.
      </Notice>
    );
  }
  if (!data.published) {
    return (
      <Notice title={data.event_name || "Coming soon"}>
        This event’s page isn’t published yet.
        {data.leaderboard_url && (
          <div style={{ marginTop: 14 }}>
            On the day, follow the scores at{" "}
            <a href={data.leaderboard_url} style={{ color: "#1F5C3F", fontWeight: 700 }}>{data.leaderboard_url.replace(/^https?:\/\//, "").replace(/^ttoleaderboard\.com/i, "TTOleaderboard.com")}</a>.
          </div>
        )}
      </Notice>
    );
  }
  return <EventPageView data={data} narrow={narrow} />;
}
